// assistantFlyFirstRun.js — A NEW WORKSPACE'S FIRST RUN, on the Fly start
// screen (fly docs/FUNNEL.md §4 G5; Nika 2026-10-24: a workspace is ACTIVATED
// when, within 7 days, ANY of four happens — a teammate invited, 5+ records
// created in the workspace's apps, the website published, 3+ workspace AI
// asks).
//
// ONE calm block, "Get started", the start screen's last part (components/
// AssistantFlyFirstRun.js): four steps that mirror the rule, each with its
// live progress, its done state and ONE action that opens the real place —
//
//   invite   the invite panel (InviteDropdown, the header's own)
//   records  the workspace's first app (the launcher's own tiles), else /apps
//   publish  the workspace's website project (the app split's one classifier),
//            else the Websites view
//   ask      the shell's composer
//
// It folds away when the workspace is activated or the person hides it (per
// person, per workspace: `user_preferences.firstRunDismissed[<workspaceId>]`).
// It is never a modal and never covers a page: it lives inside the start
// screen, which a `?fly=` page replaces on arrival.
//
// THE DATA IS THE SERVER'S. One read, through the SDK only (lane G1, sdk
// 1adab22): `sdk.getService('growth').activation(workspaceId)` → GET
// /core/workspaces/:workspaceId/activation — any member of the workspace; the
// server's evaluateActivation, the SAME function as the funnel's F8. The answer
// (the SDK's _call returns the `data` of `{ success, data }`):
//   { workspaceId, since, windowEndsAt, activated, activatedAt, activatedBy,
//     activationDays, steps: { invited, records: { count, needed },
//                              sitePublished, aiAsks: { count, needed } } }
// The client computes nothing of the rule: the window (`windowEndsAt`, the
// workspace's creation + 7 days), every count and its bar (`needed`; the
// server saturates `count` at it), `invited`, `sitePublished` and `activated`
// are the answer's (assistantFlyFirstRunView reads them). A counted step is
// done when the server's count stands at the server's bar.
//
// FEATURE-DETECTED. Until the shell's SDK has the read (the release manager's
// SDK release that carries sdk 1adab22), `getService('growth')` refuses (no
// such service) or the service has no `activation`: no request, no block — the
// start screen is exactly what it was. A null client, a failed read, a 403 or
// a 404 hide it the same way (a failure is asked again after five minutes).
//
// Arriving from Fly (`user_preferences.onboarding.via === 'fly'` — or
// `origin === 'fly'` once the tour was taken from the ready screen — written by
// Make it real on fly.symbols.app, fly docs/SYMBOLS.md §5): the shell's full
// welcome stays closed (functions/onboarding.js: a Fly record is a decision,
// or — while it is `pending` — owes the ONE ready screen of
// functions/onboardingReady.js), and the block says what Fly already made —
// "Fly set up <ws> with you", "Your apps are ready", "Your website project is
// ready — publish it" — instead of asking for it again.
//
// FRANK: every export is self-contained — no import, no module constant, no
// bare sibling call (`this.call`). `this` is the calling element.

// THE READ — asked while the start screen shows (the block's own onRender and
// stateDeps), at most once a minute per workspace (five minutes after a
// failure); `opts.force` asks again at once (the invite panel closed). One read
// in flight per workspace; an answer for a workspace that is no longer active
// drops itself. → a promise of the stored entry (null when nothing was asked).
//   root._flyFirstRun = { ws, status: 'ok' | 'error', at, data, code, wasOpen }
// `wasOpen`: an earlier answer of this page said the steps are open — so an
// activation that lands now shows its done state instead of vanishing under
// the person's eyes (Rule 66); on the next load an activated workspace shows
// nothing.
export function assistantFlyFirstRunRead(root, opts) {
  const OK_MS = 60 * 1000
  const FAIL_MS = 5 * 60 * 1000
  const o = opts && typeof opts === 'object' ? opts : {}
  const r = root || (this && this.state && this.state.root) || null
  if (!r || typeof r !== 'object' || typeof r.update !== 'function') return Promise.resolve(null)
  const wsOf = (x) => {
    const w = x && x.workspace
    return String((w && (w.id || w._id)) || '')
  }
  const wsId = wsOf(r)
  if (!wsId || !r.workspaceReady) return Promise.resolve(null)
  let sdk = o.sdk || (this && this.sdk) || null
  if (!sdk && this && typeof this.call === 'function') {
    try {
      const fns = (this.context && this.context.functions) || {}
      sdk = typeof fns.getSDK === 'function' ? this.call('getSDK') : null
    } catch (_) {
      sdk = null
    }
  }
  if (!sdk || sdk.isNullClient === true || typeof sdk.getService !== 'function') {
    return Promise.resolve(null)
  }
  const now = Date.now()
  const cur = r._flyFirstRun && typeof r._flyFirstRun === 'object' ? r._flyFirstRun : null
  if (!o.force && cur && cur.ws === wsId && cur.at > 0) {
    const age = now - cur.at
    if (age >= 0 && age < (cur.status === 'ok' ? OK_MS : FAIL_MS)) return Promise.resolve(cur)
  }
  const flight = r.__flyFirstRunFlight
  if (flight && flight.ws === wsId && flight.promise) return flight.promise
  const opened = (entry) => {
    const d = entry && entry.status === 'ok' && entry.data
    if (!d || d.activated === true) return false
    const ends = Date.parse(String(d.windowEndsAt || ''))
    return Number.isFinite(ends) && ends > Date.now()
  }
  const answered = (res) => {
    const bare = res && typeof res === 'object' && !Array.isArray(res) ? res : null
    const data =
      bare && bare.data && typeof bare.data === 'object' && !Array.isArray(bare.data)
        ? bare.data
        : bare
    const ok = !!data && typeof data.activated === 'boolean'
    return {
      ws: wsId,
      status: ok ? 'ok' : 'error',
      at: Date.now(),
      data: ok ? data : null,
      code: ok ? '' : 'shape'
    }
  }
  const failed = (err) => ({
    ws: wsId,
    status: 'error',
    at: Date.now(),
    data: null,
    code: String((err && (err.status || err.code)) || 'failed')
  })
  const promise = Promise.resolve()
    // The feature gate: the service and its method themselves (the shell's
    // init-await proxy answers getService with a promise; an SDK without the
    // service rejects it), never a version guess.
    .then(() => sdk.getService('growth'))
    .then(
      (svc) => (svc && typeof svc.activation === 'function' ? svc : null),
      () => null
    )
    .then((svc) =>
      svc
        ? Promise.resolve()
            .then(() => svc.activation(wsId))
            .then(answered, failed)
        : null
    )
    .then((entry) => {
      if (r.__flyFirstRunFlight && r.__flyFirstRunFlight.promise === promise) {
        r.__flyFirstRunFlight = null
      }
      // no read in this SDK: nothing asked, nothing stored, no block
      if (!entry) return null
      // another workspace is on screen now: this answer is not its
      if (wsOf(r) !== wsId) return null
      const prev = r._flyFirstRun && r._flyFirstRun.ws === wsId ? r._flyFirstRun : null
      const next = { ...entry, wasOpen: !!(prev && (prev.wasOpen || opened(prev))) }
      r.update({ _flyFirstRun: next }, { preventFetch: true })
      return next
    })
  r.__flyFirstRunFlight = { ws: wsId, promise }
  return promise
}

// The block's own prime (its onRender and every stateDeps change): a read
// only while the start screen SHOWS — the screen stays mounted, hidden, on
// every home, and a hidden block must never ask.
export function assistantFlyFirstRunPrime(el, s) {
  const host = el || this
  const root = (s && s.root) || (host && host.state && host.state.root) || null
  if (!host || typeof host.call !== 'function' || !root) return false
  const fns = (host.context && host.context.functions) || {}
  if (typeof fns.assistantFlyStartShown === 'function') {
    if (!host.call('assistantFlyStartShown', root)) return false
  }
  Promise.resolve(host.call('assistantFlyFirstRunRead', root)).catch((err) =>
    console.warn('[fly] first-run read failed:', err && err.message)
  )
  return true
}

// THE VIEW — what the block shows, from the read and the person's own record
// (pure over `root`; `now` is a test seam). Hidden unless the answer is this
// workspace's, the record is read (a hide on another device must never
// flash here) and the person did not hide it for this workspace:
//   open   not activated, inside the window the answer names
//   done   activated while this page showed the steps (`wasOpen`) — the done
//          step ticked, "You're all set"; gone on the next load
// A step the answer says does not count here is left out: `applies.<field>`
// false, else (an answer without `applies`) the step itself `null` — the
// invite in a PERSONAL workspace (personal orgs cannot invite; Nika
// 2026-10-24 "Other 3 steps only"): three steps there, four in a team
// workspace. The server decides it; the client never reads the workspace's
// kind for it, and an older answer (neither mark) shows all four.
// → { show, mode: 'open' | 'done' | '', title, lead, hideLabel, cols,
//     steps: [row] }   (`cols` — how many steps, the grid's tracks)
// Every row carries EVERY key the step reads (a childrenAs-'state' child falls
// back to root state for a key it lacks).
export function assistantFlyFirstRunView(root, now) {
  const KEYS = {
    title: 'workspace.firstRun.title',
    titleDone: 'workspace.firstRun.titleDone',
    lead: 'workspace.firstRun.lead',
    leadFly: 'workspace.firstRun.leadFly',
    leadDone: 'workspace.firstRun.leadDone',
    hide: 'workspace.firstRun.hide',
    progress: 'workspace.firstRun.progress',
    done: 'workspace.firstRun.stepDone'
  }
  // One row per step of the answer (`steps.<field>`), in the rule's order. A
  // `counted` step is { count, needed }; the others are a flag. `ready` copy
  // says what the workspace already has (Fly made it, or the person did)
  // instead of asking for it; `actionReady` names the place the press opens.
  const STEPS = [
    {
      step: 'invite',
      field: 'invited',
      counted: false,
      key: 'firstRunInvite',
      icon: 'userPlus',
      title: 'workspace.firstRun.invite.title',
      hint: 'workspace.firstRun.invite.hint',
      hintReady: '',
      action: 'workspace.firstRun.invite.action',
      actionReady: ''
    },
    {
      step: 'records',
      field: 'records',
      counted: true,
      key: 'firstRunRecords',
      icon: 'database',
      title: 'workspace.firstRun.records.title',
      hint: 'workspace.firstRun.records.hint',
      hintReady: 'workspace.firstRun.records.hintReady',
      action: 'workspace.firstRun.records.actionApps',
      actionReady: 'workspace.firstRun.records.action'
    },
    {
      step: 'publish',
      field: 'sitePublished',
      counted: false,
      key: 'firstRunPublish',
      icon: 'globe',
      title: 'workspace.firstRun.publish.title',
      hint: 'workspace.firstRun.publish.hint',
      hintReady: 'workspace.firstRun.publish.hintReady',
      action: 'workspace.firstRun.publish.actionList',
      actionReady: 'workspace.firstRun.publish.action'
    },
    {
      step: 'ask',
      field: 'aiAsks',
      counted: true,
      key: 'firstRunAsk',
      icon: 'magicstar',
      title: 'workspace.firstRun.ask.title',
      hint: 'workspace.firstRun.ask.hint',
      hintReady: '',
      action: 'workspace.firstRun.ask.action',
      actionReady: ''
    }
  ]
  const HIDDEN = { show: false, mode: '', title: '', lead: '', hideLabel: '', cols: 0, steps: [] }
  const r = root && typeof root === 'object' ? root : {}
  const w = r.workspace
  const wsId = String((w && (w.id || w._id)) || '')
  const entry = r._flyFirstRun && typeof r._flyFirstRun === 'object' ? r._flyFirstRun : null
  if (!wsId || !entry || entry.ws !== wsId || entry.status !== 'ok' || !entry.data) return HIDDEN
  const prefs = r.userPrefs && typeof r.userPrefs === 'object' ? r.userPrefs : null
  if (!prefs) return HIDDEN
  const hidden = prefs.firstRunDismissed
  if (hidden && typeof hidden === 'object' && hidden[wsId]) return HIDDEN
  // A personalized sign-up's workspace has its own first thread and its own
  // "Getting started" (functions/assistantFlyResearch.js, POB S7): this block
  // steps aside there. For a POB record — and a Fly one (the sign-up race: a
  // Fly write can win the write-once record) — the slot WAITS for the build
  // board's first answer for this workspace and shows nothing until it is in,
  // so "Get started" never shows and then goes (the POB lead 2026-10-30):
  // a board (200) — the first thread takes the slot; no board (a 404, or a
  // 200 with no data) — "Get started". A failed read is no answer (the first
  // thread's prime asks again). An SDK that cannot read a board never
  // answers, so it never holds the slot.
  const SIGNUPS = ['pob', 'fly']
  const ob = prefs.onboarding && typeof prefs.onboarding === 'object' ? prefs.onboarding : null
  if (ob && (SIGNUPS.indexOf(ob.via) !== -1 || SIGNUPS.indexOf(ob.origin) !== -1)) {
    const board = r._pobBuild && typeof r._pobBuild === 'object' ? r._pobBuild : null
    const answered = !!(
      board &&
      board.ws === wsId &&
      (board.status === 'ok' || board.status === 'none')
    )
    if (answered && board.status === 'ok') return HIDDEN
    if (!answered) {
      const fns = (this && this.context && this.context.functions) || {}
      let sdk = null
      if (typeof fns.getSDK === 'function') {
        try {
          sdk = this.call('getSDK')
        } catch (_) {
          sdk = null
        }
      }
      if (sdk && sdk.isNullClient !== true && typeof sdk.getOnboardingBuild === 'function') {
        return HIDDEN
      }
    }
  }
  const data = entry.data
  const t = Number(now) || Date.now()
  let mode = ''
  if (data.activated === true) {
    if (entry.wasOpen) mode = 'done'
  } else {
    const ends = Date.parse(String(data.windowEndsAt || ''))
    if (Number.isFinite(ends) && ends > t) mode = 'open'
  }
  if (!mode) return HIDDEN

  const word = (key, params) => {
    if (!key) return ''
    const raw = this.call('polyglot', key)
    let out = raw == null ? '' : String(raw)
    const p = params && typeof params === 'object' ? params : {}
    for (const name of Object.keys(p)) out = out.split('{' + name + '}').join(String(p[name]))
    return out
  }
  const scope = String(this.call('assistantScopeName', r) || '')
  // Fly's record: pending or done via 'fly'; or `origin: 'fly'` once the person
  // took the tour from the ready screen (functions/onboarding.js onboardingDecide)
  const viaFly = !!(
    prefs.onboarding &&
    (prefs.onboarding.via === 'fly' || prefs.onboarding.origin === 'fly')
  )
  const fns = (this.context && this.context.functions) || {}
  // what the workspace already has (the shell's own lists — never the rule):
  // its apps (the launcher's custom tiles) and its website projects
  const tiles =
    typeof fns.visibleStarterTiles === 'function' ? this.call('visibleStarterTiles', r) || [] : []
  const app = tiles.find((x) => x && x.custom && x.route) || null
  // "Open Commerce" — an installed app's tile (it carries `appKey`) by its App
  // Store name, through installedAppName (Nika 2026-10-23: "app names = what
  // a person sees in the workspace App Store"); a customer's own tile reads
  // its words through polyglot, as before.
  const appName = app
    ? String(
        (app.appKey && this.call('installedAppName', { appKey: app.appKey, title: app.title })) ||
          ''
      ) || word(app.title)
    : ''
  const sites = this.call('assistantFlyFirstRunSites', r) || []
  const answer = data.steps && typeof data.steps === 'object' ? data.steps : {}
  const applies = data.applies && typeof data.applies === 'object' ? data.applies : {}
  const num = (v) => {
    if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null
    const n = Number(v)
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null
  }
  // the server's two marks of a step that does not count here: `applies`
  // false, else (an answer without `applies`) the step itself null
  const counts = (def) =>
    typeof applies[def.field] === 'boolean' ? applies[def.field] : answer[def.field] !== null
  const steps = STEPS.filter(counts).map((def) => {
    const v = answer[def.field]
    const c = def.counted && v && typeof v === 'object' ? v : {}
    const count = num(c.count)
    const target = num(c.needed)
    // a flag step is done when the server says so; a counted one when the
    // server's count stands at the server's bar
    const done = def.counted
      ? count != null && target != null && target > 0 && count >= target
      : v === true
    const ready = (def.step === 'records' && !!app) || (def.step === 'publish' && sites.length > 0)
    const title = word(def.title)
    const action = done
      ? word(KEYS.done)
      : word(ready && def.actionReady ? def.actionReady : def.action, {
          app: appName
        })
    return {
      key: def.key,
      step: def.step,
      icon: done ? 'checkmark' : def.icon,
      done,
      title,
      hint: word(ready && def.hintReady ? def.hintReady : def.hint, { workspace: scope }),
      // the count of a step that needs more than one (5 records, 3 asks), as
      // the server counted it — never a count of the client's own
      progress:
        !done && count != null && target != null && target > 1
          ? word(KEYS.progress, { n: Math.min(count, target), total: target })
          : '',
      action
    }
  })
  return {
    show: true,
    mode,
    title: word(mode === 'done' ? KEYS.titleDone : KEYS.title),
    lead: word(mode === 'done' ? KEYS.leadDone : viaFly ? KEYS.leadFly : KEYS.lead, {
      workspace: scope
    }),
    hideLabel: word(KEYS.hide),
    cols: steps.length,
    steps
  }
}

// The workspace's website projects, newest first — the app split's ONE
// classifier (shared projectKind: splitProjectsByKind / projectKindContext)
// over the org's projects the home already loaded (`_projectsForActiveOrg`),
// this workspace's only. → [{ owner, key, name }]
export function assistantFlyFirstRunSites(root) {
  const r = root && typeof root === 'object' ? root : {}
  const fns = (this && this.context && this.context.functions) || {}
  const all = Array.isArray(r._projectsForActiveOrg) ? r._projectsForActiveOrg : []
  if (!all.length) return []
  if (
    typeof fns.splitProjectsByKind !== 'function' ||
    typeof fns.projectKindContext !== 'function'
  ) {
    return []
  }
  const w = r.workspace
  const wsId = String((w && (w.id || w._id)) || '')
  const scoped = all.filter((p) => {
    const id =
      p && p.workspace && typeof p.workspace === 'object'
        ? p.workspace.id || p.workspace._id
        : p && p.workspace
    return !!wsId && String(id || '') === wsId
  })
  const split = this.call('splitProjectsByKind', scoped, this.call('projectKindContext', r)) || {}
  const sites = Array.isArray(split.websites) ? split.websites : []
  const at = (p) => Date.parse(String((p && (p.updatedAt || p.createdAt)) || '')) || 0
  return sites
    .slice()
    .sort((a, b) => at(b) - at(a))
    .map((p) => ({
      owner: String(this.call('ownerSlug', p) || ''),
      key: String(this.call('projectKey', p) || ''),
      name: String((p && p.name) || '')
    }))
    .filter((p) => p.owner && p.key)
}

// A step, pressed: its ONE action, the real place. `s` is the step's row,
// `el` the pressed card (the invite panel anchors to it).
//   invite   the header's invite panel; when it closes the read is asked again
//   records  the workspace's first app (its route), else the Apps view
//   publish  the website project (its canvas, where Publish is), else Websites
//   ask      the composer
export function assistantFlyFirstRunOpen(s, el) {
  const row = s && typeof s.parse === 'function' ? s.parse() : s
  const host = el || this
  const root = host && host.state && host.state.root
  if (!row || !row.step || row.done || !root) return false
  if (row.step === 'invite') {
    host.call('openDropdown', {
      anchorEl: host,
      id: 'appInvite',
      placement: 'bottom-start',
      round: 'radiusCard',
      content: { extends: 'InviteDropdown' },
      onClose: () => {
        Promise.resolve(host.call('assistantFlyFirstRunRead', root, { force: true })).catch(
          () => null
        )
      }
    })
    return true
  }
  if (row.step === 'records') {
    const tiles = host.call('visibleStarterTiles', root) || []
    const app = tiles.find((x) => x && x.custom && x.route) || null
    host.call('openInSlot', app ? app.route : '/apps', host)
    return true
  }
  if (row.step === 'publish') {
    const site = (host.call('assistantFlyFirstRunSites', root) || [])[0]
    const href = site ? host.call('projectHref', root, site.owner, site.key) : null
    host.call('openInSlot', href || '/studio', host)
    return true
  }
  if (row.step === 'ask') return !!host.call('focusShellComposer')
  return false
}

// HIDE — the person's own choice, for THIS workspace (the other workspaces
// keep theirs): `user_preferences.firstRunDismissed[<workspaceId>] = ISO`,
// written the way every user_preferences writer writes (the home-prefs
// durability law, functions/slotSheetWidth.js): this session's row first (the
// block leaves at once), then the record on the SINGLE-FLIGHT queue every
// writer of the row shares (`root.__navbarSaveQueue`), the payload merged onto
// a FRESH read at send time. `opts`: { root, sdk, now } — test seams.
export function assistantFlyFirstRunDismiss(opts) {
  const o = opts && typeof opts === 'object' ? opts : {}
  const el = this && typeof this === 'object' ? this : null
  const root = o.root || (el && el.state && el.state.root) || null
  if (!root || typeof root.update !== 'function') return Promise.resolve(null)
  const w = root.workspace
  const wsId = String((w && (w.id || w._id)) || '')
  if (!wsId) return Promise.resolve(null)
  const at = new Date(typeof o.now === 'number' ? o.now : Date.now()).toISOString()
  const mark = (row) => {
    const base = row && typeof row === 'object' ? row : {}
    const bag =
      base.firstRunDismissed && typeof base.firstRunDismissed === 'object'
        ? base.firstRunDismissed
        : {}
    return { ...base, firstRunDismissed: { ...bag, [wsId]: bag[wsId] || at } }
  }
  const prefs = root.userPrefs && typeof root.userPrefs === 'object' ? root.userPrefs : null
  if (prefs) root.update({ userPrefs: mark(prefs) }, { preventFetch: true })
  const sdk = o.sdk || (el && (el.sdk || (el.context && el.context.sdk))) || null
  if (!sdk || typeof sdk.execute !== 'function') return Promise.resolve(null)
  if (!root.__navbarSaveQueue) root.__navbarSaveQueue = new Map()
  const queue = root.__navbarSaveQueue
  const prior = queue.get(wsId) || Promise.resolve()
  const task = prior
    .catch(() => {})
    .then(async () => {
      // the FRESH read: the record as the server holds it now
      const got = await sdk.execute('workspaceProject.userPreferences', 'get')
      const fresh = got && typeof got === 'object' ? got : {}
      const live = root.userPrefs && typeof root.userPrefs === 'object' ? root.userPrefs : {}
      const payload = mark({ ...live, ...fresh })
      const row = await sdk.execute('workspaceProject.userPreferences', 'update', { payload })
      const now = root.userPrefs && typeof root.userPrefs === 'object' ? root.userPrefs : null
      root.update(
        { userPrefs: now ? mark(now) : row && typeof row === 'object' ? row : payload },
        { preventFetch: true }
      )
      return row
    })
  queue.set(wsId, task)
  return task.catch((err) => {
    console.warn('[fly] first-run hide save failed:', err && err.message)
    return null
  })
}
